import React, {useState} from 'react';
import TextInput from '../../../../uiToolkit/Inputs/TextInput';
import TextAreaInput from '../../../../uiToolkit/Inputs/TextAreaInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import {Icon} from '../../../../uiToolkit/Icon/Icon';
import {MediaFile} from '../../../../data/models/LibraryFile/MediaFile';
import {
  POST_CAPTION_MAX_LENGTH,
  POST_MAX_ITEMS,
  POST_TITLE_MAX_LENGTH,
  PostMediaItem,
} from '../../../../data/models/Posts/Post';
import {postsText} from '../../postsText';
import PostMediaList from '../components/PostMediaList';
import PostPreviewOverlay from '../../components/PostPreviewOverlay';
import styles from './CreatePostUI.module.css';

interface Props {
  items: MediaFile[];
  title: string;
  caption: string;
  isLoading: boolean;
  onTitleChange: (value: string) => void;
  onCaptionChange: (value: string) => void;
  onSelectMedia: () => void;
  onMoveItem: (index: number, direction: 1 | -1) => void;
  onRemoveItem: (fileId: string) => void;
  onCreate: () => void;
  onCancel: () => void;
  projectName?: string;
}

const toMediaItem = (file: MediaFile): PostMediaItem => ({
  id: file.id,
  isVideo: file.fileType.startsWith('video'),
  title: file.fileName,
  url: file.fileUrl,
  poster: file.placeholderUrl,
});

const CreatePostUI = React.memo(
  ({
    items,
    title,
    caption,
    isLoading,
    onTitleChange,
    onCaptionChange,
    onSelectMedia,
    onMoveItem,
    onRemoveItem,
    onCreate,
    onCancel,
    projectName,
  }: Props) => {
    const [previewShown, setPreviewShown] = useState(false);

    const createEnabled = !!title.trim() && items.length > 0 && !isLoading;

    return (
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button className={styles.cancelButton} onClick={onCancel}>
            ✕ {postsText.cancel}
          </button>
          <div className={styles.topBarActions}>
            <button
              className={styles.textAction}
              onClick={() => setPreviewShown(true)}
              disabled={!items.length}>
              {postsText.preview}
            </button>
            <button
              className={styles.createButton}
              onClick={onCreate}
              disabled={!createEnabled}>
              {isLoading ? (
                <span className={styles.createButtonLoading}>
                  <LoadingSpinner size={18} />
                  {postsText.createPost}
                </span>
              ) : (
                postsText.createPost
              )}
            </button>
          </div>
        </div>

        <h1
          className={`${styles.pageTitle} ${
            projectName ? styles.pageTitleCompact : ''
          }`}>
          {postsText.createTitle}
        </h1>
        {projectName && (
          <p className={styles.pageSubtitle}>
            {postsText.inProject(projectName)}
          </p>
        )}

        <div className={styles.body}>
          <div className={styles.sourceColumn}>
            <div className={styles.sourceCard}>
              <div className={styles.sourceHeader}>
                <h2 className={styles.sourceTitle}>{postsText.sourceTitle}</h2>
                {!!items.length && (
                  <span className={styles.itemsCount}>
                    {postsText.itemsCount(items.length, POST_MAX_ITEMS)}
                  </span>
                )}
              </div>
              {items.length ? (
                <>
                  <PostMediaList
                    items={items}
                    onMove={onMoveItem}
                    onRemove={onRemoveItem}
                  />
                  <div className={styles.sourceActions}>
                    <Button
                      title={postsText.addMore}
                      onClick={onSelectMedia}
                      uiType="secondary"
                      disabled={items.length >= POST_MAX_ITEMS}
                    />
                  </div>
                </>
              ) : (
                <div className={styles.emptySource}>
                  <Icon
                    name="posts-menu.svg"
                    className={styles.emptySourceIcon}
                  />
                  <div className={styles.emptySourceText}>
                    <p className={styles.emptySourceTitle}>
                      {postsText.sourceEmptyTitle}
                    </p>
                    <p className={styles.emptySourceHint}>
                      {postsText.sourceHint}
                    </p>
                    <p className={styles.emptySourceHint}>
                      {postsText.sourceLimitHint(POST_MAX_ITEMS)}
                    </p>
                  </div>
                  <Button
                    title={postsText.selectMedia}
                    onClick={onSelectMedia}
                    uiType="secondary"
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.formColumn}>
            <TextInput
              value={title}
              onChange={onTitleChange}
              placeholder={postsText.postTitlePlaceholder}
              heading={postsText.postTitle}
              maxCharacters={POST_TITLE_MAX_LENGTH}
              isMandatory
              className={styles.field}
            />
            <TextAreaInput
              value={caption}
              onChange={onCaptionChange}
              placeholder={postsText.captionPlaceholder}
              heading={postsText.caption}
              maxCharacters={POST_CAPTION_MAX_LENGTH}
              className={styles.field}
            />
          </div>
        </div>

        <PostPreviewOverlay
          shown={previewShown}
          items={items.map(toMediaItem)}
          title={title || postsText.previewTitle}
          caption={caption}
          onClose={() => setPreviewShown(false)}
        />
      </div>
    );
  },
);

export default CreatePostUI;
